import { useInView } from '../../hooks/useInView';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
  detail?: string;
}

interface ProcessProps {
  eyebrow?: string;
  headline: string;
  steps: ProcessStep[];
  ctaText?: string;
  ctaHref?: string;
}

export default function SectionProcess({
  eyebrow = 'Our Process',
  headline,
  steps,
  ctaText = 'Begin Your Journey',
  ctaHref = '#contact',
}: ProcessProps) {
  const { ref, inView } = useInView();

  return (
    <section id="process" className="bg-[#060F1D] py-28 md:py-40 relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,180,216,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,180,216,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />
      {/* Gradient blobs */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[300px] bg-[#00B4D8]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-[#00B4D8]/4 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={`text-center mb-24 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-14 bg-gradient-to-r from-transparent to-[#00B4D8]" />
            <span className="text-[#00B4D8] text-[10px] font-black uppercase tracking-[0.4em]">{eyebrow}</span>
            <div className="h-px w-14 bg-gradient-to-l from-transparent to-[#00B4D8]" />
          </div>
          <h2
            className="text-4xl md:text-[56px] font-black text-white leading-[1.05] max-w-3xl mx-auto"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {headline}
          </h2>
        </div>

        {/* Steps — asymmetric layout */}
        <div className="relative max-w-5xl mx-auto">
          {/* Center vertical line */}
          <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-8 bottom-8 w-px bg-gradient-to-b from-[#00B4D8]/40 via-[#00B4D8]/20 to-[#00B4D8]/40" />

          <div className="space-y-16 lg:space-y-0">
            {steps.map((step, i) => {
              const isLeft = i % 2 === 0;
              return (
                <div
                  key={step.number}
                  className={`flex flex-col lg:flex-row items-center gap-8 lg:gap-0 lg:mb-16 ${
                    !isLeft ? 'lg:flex-row-reverse' : ''
                  }`}
                  style={{
                    transitionDelay: `${i * 150}ms`,
                  }}
                >
                  {/* Content side */}
                  <div className={`w-full lg:w-[calc(50%-48px)] ${isLeft ? 'lg:pr-12 lg:text-right' : 'lg:pl-12 lg:text-left'}`}>
                    <div className={`inline-flex flex-col items-start ${isLeft ? 'lg:items-end' : 'lg:items-start'}`}>
                      <span
                        className="text-white/5 font-black leading-none select-none"
                        style={{ fontFamily: "'Playfair Display', serif", fontSize: '90px', lineHeight: 1 }}
                      >
                        {step.number}
                      </span>
                      <div className={`-mt-6 ${isLeft ? 'lg:text-right' : 'lg:text-left'}`}>
                        <div className={`flex items-center gap-2 mb-3 ${isLeft ? 'lg:justify-end' : 'lg:justify-start'}`}>
                          <span className="text-[#00B4D8] text-[10px] font-black uppercase tracking-[0.3em]">
                            Step {i + 1}
                          </span>
                        </div>
                        <h3
                          className="text-2xl font-black text-white mb-3 leading-snug"
                          style={{ fontFamily: "'Playfair Display', serif" }}
                        >
                          {step.title}
                        </h3>
                        <p className="text-white/45 text-[15px] leading-relaxed max-w-[320px]">
                          {step.description}
                        </p>
                        {step.detail && (
                          <p className="text-white/25 text-sm leading-relaxed mt-2 max-w-[320px]">{step.detail}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Center node */}
                  <div className="shrink-0 lg:w-24 flex items-center justify-center z-10 relative">
                    <div className="w-16 h-16 rounded-full border-2 border-[#00B4D8]/40 bg-[#060F1D] flex items-center justify-center hover:border-[#00B4D8] hover:bg-[#00B4D8]/10 transition-all duration-400 cursor-default group">
                      <div className="w-10 h-10 flex items-center justify-center">
                        <i className={`${step.icon} text-[#00B4D8] text-2xl group-hover:scale-110 transition-transform`} />
                      </div>
                    </div>
                  </div>

                  {/* Spacer side */}
                  <div className="hidden lg:block w-[calc(50%-48px)]" />
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-20">
          <a
            href={ctaHref}
            className="inline-flex items-center gap-3 bg-[#00B4D8] hover:bg-[#00C9F0] text-white font-bold px-12 rounded-full transition-all duration-300 hover:-translate-y-1 whitespace-nowrap cursor-pointer text-[15px]"
            style={{ paddingTop: '20px', paddingBottom: '20px' }}
          >
            {ctaText}
            <i className="ri-arrow-right-line" />
          </a>
          <p className="text-white/25 text-sm mt-4">No obligation — speak with a dive specialist first</p>
        </div>
      </div>
    </section>
  );
}
